import { useCallback, useMemo, useState } from "react";
import { Empty } from "antd";
import { useJourneyDynamicDetails, useJourneyStaticDetails } from "../../../../context";
import { explode } from "src/utility";
import { getSeatMapping } from "../../utils";
import { useSSRContext } from "../../context/SSRContext";
import Legend from "./components/Legend/Legend";
import DeckSelection from "./components/DeckSelection/DeckSelection";
import SeatSymbolDefs from "./components/SeatIcon/SeatSymbolDefs";
import { PLANE_SEATING_LEGEND, RowSeat, SeatType } from "src/Modules/Flights/constants/JourneyDetails";
import PlaneSeatingCommon from "./layouts/PlaneSeating.common";
import Sticky from "src/components/Sticky";

export default function PlaneSeating() {
  const { cursor, flightOptions, updateSSRItem } = useSSRContext();
  const { journeyDetails } = useJourneyStaticDetails();
  const { passengersState } = useJourneyDynamicDetails();
  const [passengers] = passengersState;
  const { data } = explode(journeyDetails, {});
  const flightSelected = flightOptions?.find((option) => option.value === cursor.flightId);
  const allRows: RowSeat[] | undefined = data
    ? data?.ssr?.seatDynamic?.[flightSelected?.segmentIndex ?? 0]?.segmentSeat[flightSelected?.flightIndex ?? 0]?.rowSeats
    : [];
  const decks = useMemo(
    () => Array.from(new Set((allRows ?? []).flatMap((row) => row.seats.filter((seat) => seat.seatNo).map((seat) => seat.deck)))).sort((a, b) => a - b),
    [allRows],
  );
  const [selectedDeck, setSelectedDeck] = useState<number>();
  const activeDeck = selectedDeck !== undefined && decks.includes(selectedDeck) ? selectedDeck : decks[0];
  const hasMultipleDecks = decks.length > 1;
  const rows = useMemo(
    () =>
      hasMultipleDecks
        ? allRows
            ?.map((row) => ({ ...row, seats: row.seats.filter((seat) => seat.deck === activeDeck) }))
            .filter((row) => row.seats.length)
        : allRows,
    [allRows, hasMultipleDecks, activeDeck],
  );
  const { colMap } = useMemo(() => getSeatMapping(rows ?? []) ?? { colMap: new Map(), gapsTotal: 0, seatsCount: 0 }, [rows]);
  const handleClick = useCallback(
    (val: SeatType) => {
      updateSSRItem("seats", val);
    },
    [updateSSRItem],
  );
  
  const craftType = rows?.[0]?.seats?.[0]?.craftType;
  const segmentIndex = flightSelected?.segmentIndex;
  const flightIndex = flightSelected?.flightIndex;
  const reservedSeats = useMemo(
    () =>
      new Set(
        passengers.passengers
          ?.map((pas) => pas?.segments[segmentIndex])
          ?.flatMap((flights) => flights?.flights[flightIndex ?? 0])
          ?.flatMap((addition) => Object.keys(addition?.ssr?.seats ?? {})),
      ),
    [passengers.passengers, segmentIndex, flightIndex],
  );
  const isEmpty = !rows?.length;

  if (isEmpty)
    return (
      <div className="flex w-full items-center justify-center">
        <Empty />
      </div>
    );

  return (
    <div className="flex w-full flex-1 flex-col gap-4">
      <SeatSymbolDefs />
      <Sticky
        className="bg-background-default gap-4"
        header={
          <div className="bg-background-default z-30">
            <Legend items={PLANE_SEATING_LEGEND} />
            <DeckSelection decks={decks} value={activeDeck} onChange={setSelectedDeck} />
          </div>
        }
        body={() => (
            <PlaneSeatingCommon
              colMap={colMap}
              rows={rows}
              craftType={craftType}
              reservedSeats={reservedSeats}
              onClick={handleClick}
              flightId={cursor.flightId}
            />
        )}
      />
    </div>
  );
}
